import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SearchBar from "./SearchBar";
import oplixiLogo from "../assets/oplixilogo.png";

const Header = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const [scrolled, setScrolled] = useState(false);

  /* Scroll-based header blur + shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Close mobile nav on route change */
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    try {
      setLogoutError('');
      await logout();
      navigate('/login');
    } catch (error) {
      setLogoutError('Failed to log out: ' + error.message);
    }
  }

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/movies', label: 'Movies' },
    { to: '/categories', label: 'Categories' },
  ];

  const isActive = (path) => location.pathname === path;

  // The fullscreen video player (/watch/:id) has its own Back button and
  // controls — hide the site header there so it doesn't cover them.
  if (location.pathname.startsWith('/watch')) {
    return null;
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[99999] w-full transition-all duration-300 ${
          scrolled
            ? 'bg-black/90 backdrop-blur-md shadow-lg shadow-black/30'
            : 'bg-gradient-to-b from-black/80 to-transparent'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center h-16 sm:h-[72px] px-4 sm:px-8 max-w-[1920px] mx-auto">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={oplixiLogo}
              alt="Oplixi"
              className="w-[120px] sm:w-[140px] h-auto block"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex gap-6 items-center ml-8">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`text-sm font-medium transition-colors ${
                  isActive(to) ? 'text-white' : 'text-gray-300 hover:text-white'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          {/* Desktop Search */}
          <div className="hidden md:block mr-4">
            <SearchBar />
          </div>

          {/* Logout Error */}
          {logoutError && (
            <div className="hidden sm:flex text-red-500 text-xs mr-3 items-center">
              {logoutError}
              <button
                onClick={() => setLogoutError('')}
                className="ml-1 text-gray-400 hover:text-white bg-transparent border-none cursor-pointer"
              >
                &times;
              </button>
            </div>
          )}

          {/* Desktop User Actions */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/mylist" className="text-gray-300 hover:text-white text-sm font-medium">
              My List
            </Link>
            <Link
              to="/profile"
              className="w-8 h-8 rounded-full bg-gradient-to-br from-netflix-red to-red-800 flex items-center justify-center text-white text-sm font-bold hover:ring-2 hover:ring-white/50 transition-all"
              title={currentUser?.email || 'Profile'}
            >
              {currentUser?.email?.charAt(0).toUpperCase() || 'G'}
            </Link>
            {currentUser && !currentUser.isGuest && (
              <button
                onClick={handleLogout}
                className="text-gray-400 hover:text-white text-sm border-none bg-transparent cursor-pointer"
              >
                Sign Out
              </button>
            )}
          </div>

          {/* Mobile: Profile icon + Hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <Link
              to="/profile"
              className="w-8 h-8 rounded-full bg-gradient-to-br from-netflix-red to-red-800 flex items-center justify-center text-white text-xs font-bold"
            >
              {currentUser?.email?.charAt(0).toUpperCase() || 'G'}
            </Link>
            <button
              className="relative w-8 h-8 flex items-center justify-center z-20 bg-transparent border-none"
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileNavOpen(open => !open)}
            >
              <span
                className={`absolute w-5 h-0.5 bg-white rounded transition-all duration-300 ${
                  mobileNavOpen ? 'rotate-45 top-[15px]' : 'top-[10px]'
                }`}
              />
              <span
                className={`absolute w-5 h-0.5 bg-white rounded transition-all duration-300 top-[15px] ${
                  mobileNavOpen ? 'opacity-0 scale-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute w-5 h-0.5 bg-white rounded transition-all duration-300 ${
                  mobileNavOpen ? '-rotate-45 top-[15px]' : 'top-[20px]'
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Nav Overlay */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-[99998] bg-black/95 backdrop-blur-sm animate-fade-in overflow-y-auto" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}>
          <div className="px-6 py-6 space-y-6">
            {/* Mobile Search */}
            <div className="w-full">
              <SearchBar />
            </div>

            {/* Nav Links */}
            <nav className="flex flex-col gap-1">
              {navLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`py-3 px-4 rounded-lg text-lg font-medium transition-colors ${
                    isActive(to)
                      ? 'text-white bg-white/10'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {label}
                </Link>
              ))}
              <Link
                to="/mylist"
                className={`py-3 px-4 rounded-lg text-lg font-medium transition-colors ${
                  isActive('/mylist')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileNavOpen(false)}
              >
                My List
              </Link>
              <Link
                to="/profile"
                className={`py-3 px-4 rounded-lg text-lg font-medium transition-colors ${
                  isActive('/profile')
                    ? 'text-white bg-white/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
                onClick={() => setMobileNavOpen(false)}
              >
                Profile
              </Link>
            </nav>

            <div className="border-t border-white/10" />

            {/* Info Links */}
            <nav className="flex flex-col gap-1">
              <p className="text-gray-600 text-xs uppercase tracking-wider px-4 mb-1">Info</p>
              {[
                { to: '/about', label: 'About Oplixi' },
                { to: '/help', label: 'Help & Support' },
                { to: '/faq', label: 'FAQ' },
                { to: '/contact', label: 'Contact' },
              ].map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`py-2.5 px-4 rounded-lg text-base font-medium transition-colors ${
                    isActive(to)
                      ? 'text-white bg-white/10'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                  onClick={() => setMobileNavOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </nav>

            <div className="border-t border-white/10" />

            {/* Account Section */}
            <div className="space-y-3">
              <p className="text-gray-500 text-sm px-4">{currentUser?.email || 'Guest'}</p>
              {currentUser && !currentUser.isGuest && (
                <button
                  onClick={() => { setMobileNavOpen(false); handleLogout(); }}
                  className="w-full text-left py-3 px-4 rounded-lg text-white bg-white/5 hover:bg-white/10 text-base font-medium border-none"
                >
                  Sign Out
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
